import {
  usePutAdminsUserIdActivate,
  usePutAdminsUserIdDeactivate,
  getGetAdminsUserIdQueryKey,
} from '@/api/generated/admin/admin'
import {
  usePutProvidersUserIdActivate,
  usePutProvidersUserIdDeactivate,
  getGetProvidersUserIdQueryKey,
} from '@/api/generated/doctor/doctor'
import { useQueryClient } from '@tanstack/react-query'
import { useAppDispatch } from '@/redux/hooks/useAppDispatch'
import { SET_MEMBER_ACTIVATION_ERROR } from '@/store/member/MemberConstants'
import { userManagementTypes } from '../Constants'
import { getMemberApiErrorPayload } from './getMemberApiErrorPayload'

/**
 * Activates or deactivates a member (admin or provider).
 *
 * Replaces the `setMemberActivation` thunk + SET_MEMBER_ACTIVATION* reducer flow. The old
 * flow flipped `members[id].IsActive` in Redux on success; the caller now refreshes the
 * member via the returned `onSuccess` callback so the displayed status stays in sync. On
 * failure the existing SET_MEMBER_ACTIVATION_ERROR reducer case is preserved by dispatching
 * it with the API error body, so the error-message UX is unchanged.
 */
export function useSetMemberActivation(userId: string, userType: string) {
  const dispatch = useAppDispatch()
  const queryClient = useQueryClient()
  const isAdmin = userType === userManagementTypes.ADMIN
  const activateAdmin = usePutAdminsUserIdActivate()
  const deactivateAdmin = usePutAdminsUserIdDeactivate()
  const activateProvider = usePutProvidersUserIdActivate()
  const deactivateProvider = usePutProvidersUserIdDeactivate()

  function mutate(willActivate: boolean, opts?: { onSuccess?: () => void; onError?: (error: unknown) => void }) {
    const activeMutation =
      isAdmin ?
        willActivate ? activateAdmin
        : deactivateAdmin
      : willActivate ? activateProvider
      : deactivateProvider

    activeMutation.mutate(
      { userId },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: isAdmin ? getGetAdminsUserIdQueryKey(userId) : getGetProvidersUserIdQueryKey(userId),
          })
          opts?.onSuccess?.()
        },
        onError: (error) => {
          dispatch({
            type: SET_MEMBER_ACTIVATION_ERROR,
            payload: getMemberApiErrorPayload(error),
            failed: true,
          })
          opts?.onError?.(error)
        },
      },
    )
  }

  const isPending =
    activateAdmin.isPending || deactivateAdmin.isPending || activateProvider.isPending || deactivateProvider.isPending

  return { mutate, isPending }
}
